import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import { XIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import modalBg from "@/assets/images/onboarding/modal-background.png";
import logoIllustration from "@/assets/images/onboarding/logo-illustration.png";
import { radius } from "@libertify/ui";

// ─── Global style reset for the modal wrapper ────────────────────────────────

const ModalGlobalReset = createGlobalStyle`
  .onboarding-welcome-modal-wrap {
    padding: 0 !important;
  }
  .onboarding-welcome-modal-wrap .ant-modal {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Inner = styled.div`
  position: relative;
  background-image: url(${modalBg});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 100px 56px 80px;

  @media (width <= 768px) {
    padding: 64px 32px 56px;
  }

  @media (width <= 480px) {
    padding: 48px 20px 40px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  color: #090e19;
  opacity: 0.45;
  border-radius: 6px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const ContentWrap = styled.div`
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Illustration = styled.img`
  max-width: 120px;
  height: auto;
  margin-bottom: 56px;

  @media (width <= 768px) {
    max-width: 80px;
    margin-bottom: 32px;
  }
`;

const Heading = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 42px;
  font-weight: 600;
  color: #090e19;
  margin: 0 0 24px;
  line-height: 1.1;
  letter-spacing: -0.04em;

  @media (width <= 768px) {
    font-size: 28px;
  }

  @media (width <= 480px) {
    font-size: 22px;
    margin-bottom: 16px;
  }
`;

const Description = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  color: #090e19;
  line-height: 1.6;
  margin: 0 0 32px;

  @media (width <= 480px) {
    font-size: 13px;
    margin-bottom: 24px;
  }
`;

const StartButton = styled.button`
  width: 100%;
  height: 44px;
  background: #14b8a6;
  border: 3px solid #3bd4ca;
  border-radius: ${radius.pill}px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #0d9488;
  }
`;

// TODO: restore once "What's a video experience" link behavior is defined
// const SubNote = styled.p`
//   font-size: 13px;
//   color: #9ca3af;
//   margin: 12px 0 0;
//   cursor: default;
// `;

// ─── Component ───────────────────────────────────────────────────────────────

export type WelcomeModalProps = {
  open: boolean;
  onStart: () => void;
  onClose: () => void;
};

const getModalWidth = () => {
  if (window.innerWidth < 768) return "90vw";
  if (window.innerWidth < 1024) return "70vw";
  return "50vw";
};

export const WelcomeModal: React.FC<WelcomeModalProps> = ({
  open,
  onStart,
  onClose,
}) => {
  const t = useTranslate();
  const [modalWidth, setModalWidth] = useState(getModalWidth);

  useEffect(() => {
    const onResize = () => setModalWidth(getModalWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <ModalGlobalReset />
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={false}
        centered
        width={modalWidth}
        wrapClassName="onboarding-welcome-modal-wrap"
        styles={{
          wrapper: { padding: 0 },
          content: { padding: 0, borderRadius: radius.box, overflow: "hidden" },
          body: { padding: 0 },
        }}
      >
        <Inner>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <XIcon size={20} />
          </CloseButton>
          <ContentWrap>
            <Illustration src={logoIllustration} alt="" />
            <Heading>{t("pages.home.onboarding.welcomeHeading")}</Heading>
            <Description>
              {t("pages.home.onboarding.welcomeDescription")}
            </Description>
            <StartButton type="button" onClick={onStart}>
              {t("pages.home.onboarding.welcomeStartCreating")}
            </StartButton>
            {/* TODO: restore "What's a video experience" link once its behavior is defined */}
            {/* <SubNote>{t("pages.home.onboarding.welcomeWhatIs")}</SubNote> */}
          </ContentWrap>
        </Inner>
      </Modal>
    </>
  );
};
